import { AnyAction } from "redux";
import { StoreError } from "../../errors";
import {
  IdResourceState,
  buildIdResourceActionTypes,
  buildIdResourceActions,
  buildIdResourceReducer,
  buildIdResourceSelectors,
  initialIdResourceState,
} from "../../util/id-resource";
import { ACTION_ANSWERS_LISTEN_CHANGE } from "./listen-change";
import { ACTION_EVENT_ANSWERS_UPDATE_RESET } from "./reset-update";
import {
  ACTION_EVENT_ANSWERS_UPDATED,
  ACTION_EVENT_ANSWERS_UPDATE_ERROR,
  ACTION_EVENT_ANSWERS_UPDATING,
} from "./update";

export type TastingAnswer = {
  id: string;
  eventId?: string | null;
  beers: (string | null)[] | undefined | null;
  asterisks: boolean[] | undefined | null;
  doubleAsterisks?: boolean[] | undefined | null;
  ratings: (number | null)[] | undefined | null;
  changes: number[] | undefined | null;
  rounds: number | undefined | null;
  // Unscored memory aids gated by the event's `reminderMode`.
  secondaryBeers?: (string | null)[] | undefined | null;
  notes?: (string | null)[] | undefined | null;
  // Marks rounds whose beer was filled by Lotto Loco (random pick).
  lottoLoco?: boolean[] | undefined | null;
};

/**
 * The id-resource state is keyed by `eventId` (the resource identifier the
 * Tasting screen knows). Previously this slice kept a flat `items: TastingAnswer[]`
 * keyed by the user's email — that worked while only one user was ever logged
 * in, but it conflated "the answer for event A" and "the answer for event B"
 * since both share the same `id` (the email). The new shape stores them
 * independently and lets the staleness-guarded request-token logic in the
 * id-resource reducer fix the A → B → A loading race.
 */
export type AnswersState = IdResourceState<TastingAnswer> & {
  update: {
    updating: boolean;
    updated: TastingAnswer | null;
    error: StoreError | null;
  };
};

export const ANSWERS_ACTION_TYPES = buildIdResourceActionTypes("answers");
export const answersActions = buildIdResourceActions<TastingAnswer>(
  ANSWERS_ACTION_TYPES
);
export const answersSelectors = buildIdResourceSelectors<
  { answers: AnswersState },
  TastingAnswer
>((root) => root.answers);

const idResourceReducer = buildIdResourceReducer<TastingAnswer, AnswersState>(
  ANSWERS_ACTION_TYPES
);

const initialState: AnswersState = {
  ...initialIdResourceState<TastingAnswer>(),
  update: {
    updating: false,
    updated: null,
    error: null,
  },
};

const commitAnswer = (
  state: AnswersState,
  answer: TastingAnswer
): AnswersState => {
  const eventId = answer.eventId;
  if (!eventId) return state;
  return {
    ...state,
    itemsById: { ...state.itemsById, [eventId]: answer },
  };
};

export default function answersReducer(
  state: AnswersState = initialState,
  action: AnyAction
): AnswersState {
  // Run the shared id-resource cases first so loading / loaded / errored /
  // removed all behave identically to every other id-resource slice — the
  // request-token staleness guard lives there.
  let next = idResourceReducer(state, action);

  switch (action.type) {
    case ACTION_EVENT_ANSWERS_UPDATING: {
      return { ...next, update: { ...next.update, updating: action.payload } };
    }
    case ACTION_EVENT_ANSWERS_UPDATED: {
      const item = action.payload as TastingAnswer;
      const withCommit = commitAnswer(next, item);
      const eventId = item.eventId;
      return {
        ...withCommit,
        ...(eventId ? {
          requestToken: { ...withCommit.requestToken, [eventId]: (withCommit.requestToken[eventId] ?? 0) + 1 },
          loadingFor: { ...withCommit.loadingFor, [eventId]: false },
          errorFor: { ...withCommit.errorFor, [eventId]: null },
        } : {}),
        update: { ...withCommit.update, updated: item, error: null },
      };
    }
    case ACTION_EVENT_ANSWERS_UPDATE_ERROR: {
      return { ...next, update: { ...next.update, error: action.payload } };
    }
    case ACTION_EVENT_ANSWERS_UPDATE_RESET: {
      return {
        ...next,
        update: { updating: false, updated: null, error: null },
      };
    }
    case ACTION_ANSWERS_LISTEN_CHANGE: {
      return commitAnswer(next, action.payload as TastingAnswer);
    }
    default:
      return next;
  }
}
